import React, { useState } from "react";
import { initiateOAuth, OAuthProvider } from "../utils/user";
import TermsModal from "./TermsModal";

interface SocialLoginButtonsProps {
  appId: string;
  color1?: string;
  color2?: string;
  skipTermsModal?: boolean; // Skip T&C modal on Login page - server handles T&C for new users
}

// Google Icon SVG
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

// Facebook Icon SVG
const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      fill="#FFFFFF"
    />
  </svg>
);

const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({
  appId,
  color1 = "#1e1b4b",
  color2,
  skipTermsModal = false,
}) => {
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [pendingProvider, setPendingProvider] = useState<OAuthProvider | null>(null);

  const handleOAuthClick = (provider: OAuthProvider) => {
    if (!appId) {
      console.error("No appId available for OAuth");
      return;
    }

    // On Login page, skip the T&C modal - server will handle it for new users
    if (skipTermsModal) {
      initiateOAuth(provider, appId);
      return;
    }

    // On Register page, show T&C modal first
    setPendingProvider(provider);
    setShowTermsModal(true);
  };

  const handleTermsAccept = () => {
    if (pendingProvider) {
      setShowTermsModal(false);
      initiateOAuth(pendingProvider, appId);
      setPendingProvider(null);
    }
  };

  const handleModalClose = () => {
    setShowTermsModal(false);
    setPendingProvider(null);
  };

  const getActionText = () => {
    if (pendingProvider === "google") return "Continue with Google";
    if (pendingProvider === "facebook") return "Continue with Facebook";
    return "Continue";
  };

  return (
    <>
      <div style={{ width: "100%", marginBottom: "24px" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            marginBottom: "20px",
          }}
        >
          {/* Google Button */}
          <button
            type="button"
            onClick={() => handleOAuthClick("google")}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              width: "100%",
              padding: "12px 16px",
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              color: "#333",
              transition: "all 0.2s ease",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#f5f5f5";
              e.currentTarget.style.borderColor = "#ccc";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "#fff";
              e.currentTarget.style.borderColor = "#ddd";
            }}
          >
            <GoogleIcon />
            Continue with Google
          </button>

          {/* Facebook Button */}
          <button
            type="button"
            onClick={() => handleOAuthClick("facebook")}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              width: "100%",
              padding: "12px 16px",
              backgroundColor: "#1877F2",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              color: "#fff",
              transition: "all 0.2s ease",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#166FE5";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "#1877F2";
            }}
          >
            <FacebookIcon />
            Continue with Facebook
          </button>
        </div>

        {/* Divider */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            marginTop: "8px",
          }}
        >
          <div
            style={{
              flex: 1,
              height: "1px",
              backgroundColor: "#ddd",
            }}
          />
          <span
            style={{
              color: "#666",
              fontSize: "14px",
              fontWeight: "400",
            }}
          >
            or continue with email
          </span>
          <div
            style={{
              flex: 1,
              height: "1px",
              backgroundColor: "#ddd",
            }}
          />
        </div>
      </div>

      {/* T&C Acceptance Modal */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={handleModalClose}
        onAccept={handleTermsAccept}
        color1={color1}
        color2={color2}
        actionText={getActionText()}
      />
    </>
  );
};

export default SocialLoginButtons;
